<script lang="ts" setup>
import { ElTree, TreeNode } from "element-plus";
import { computed, ref } from "vue";
import { LocationInfo, QuadrantInfo } from "../types/plusTypes";

// --- Types ---
type BaseTreeNode = TreeNode & {
  type: "location" | "quadrant";
  id: string | number;
  label: string;
  disabled: boolean;
  numberAvailable: number;
};
type QuadrantTreeNode = BaseTreeNode & {
  type: "quadrant";
  quadrant: string;
  children: LocationTreeNode[];
};
type LocationTreeNode = BaseTreeNode & {
  type: "location";
  location: string;
  quadrant: string;
};
type QuadrantLocationTreeNode = QuadrantTreeNode | LocationTreeNode;

type TreeSlotProps = {
  data: QuadrantLocationTreeNode;
};
// -------------

const props = defineProps<{
  selectedLocations: string[];
  quadrants: QuadrantInfo[];
  locations: LocationInfo[];

  validLocationNames: string[];
  disabled?: boolean;
}>();

const emit = defineEmits<{
  (e: "update:selectedLocations", value: string[]): void;
}>();

const treeRef = ref<InstanceType<typeof ElTree>>();
const quadrantLocationTree = computed<QuadrantLocationTreeNode[]>(() =>
  props.quadrants.map(quadrantToTreeNode),
);

// --- Utility Functions ---

const quadrantToTreeNode = (
  quadrant: QuadrantInfo,
): QuadrantLocationTreeNode => {
  const children: LocationTreeNode[] = props.locations
    .filter((location) => location.quadrant === quadrant.quadrant)
    .map(locationToTreeNode);
  return {
    type: "quadrant",
    id: quadrant.quadrant,
    label: quadrant.quadrant,
    quadrant: quadrant.quadrant,
    children: children,
    disabled: !!props.disabled,
    numberAvailable: children.reduce((acc, v) => acc + v.numberAvailable, 0),
  };
};

const locationToTreeNode = (location: LocationInfo): LocationTreeNode => {
  const numberAvailable = props.validLocationNames.filter(
    (loc) => loc === location.location,
  ).length;
  return {
    type: "location",
    id: location.location,
    label: location.location,
    location: location.location,
    quadrant: location.quadrant,
    disabled: !!props.disabled,
    numberAvailable: numberAvailable,
  };
};
// -------------------------

// ---- Event Handlers -----
const handleCheck = (
  _node: QuadrantLocationTreeNode,
  treeState: { checkedKeys: string[] },
) => {
  const locationNames = props.locations.map((loc) => loc.location);
  const checkedLocations = treeState.checkedKeys.filter((key) =>
    locationNames.includes(key),
  );

  emit("update:selectedLocations", checkedLocations);
};

// -------------------------
</script>
<template>
  <el-tree
    ref="treeRef"
    :data="quadrantLocationTree"
    :default-checked-keys="selectedLocations"
    :default-expanded-keys="selectedLocations"
    :expand-on-click-node="false"
    node-key="id"
    class="quadrant-location-tree"
    :indent="28"
    show-checkbox
    :class="{
      'none-filtered': selectedLocations.length === 0,
    }"
    :disabled="disabled"
    @check="handleCheck"
  >
    <template #default="{ data }: TreeSlotProps">
      <div
        :key="data.label"
        :class="{
          'quadrant-location-disabled': data.disabled,
          'hide-icon': data.type === 'location',
        }"
        class="el-checkbox__label quadrant-location-label"
      >
        {{ data.label }}
        <span class="number-available"> ({{ data.numberAvailable }}) </span>
      </div>
    </template>
  </el-tree>
</template>
<style lang="less">
.quadrant-location-tree.el-tree {
  background: transparent;

  .el-tree-node__expand-icon.expanded {
    transform: rotate(90deg);
  }

  // this code hides the expand icon for locations
  // (they have no children so it is a bit confusing to show it)
  .el-tree-node__content {
    display: flex;
    align-items: center;
    height: auto;
    background: transparent;

    .el-checkbox__label {
      padding-left: 0;
      white-space: normal;
      background: transparent;
    }
    gap: 8px;
    &:has(> .el-icon) {
      &:has(> .hide-icon) {
        > .el-icon {
          display: none;
        }

        // to maintain the same margin as the rest of the tree nodes
        margin-left: 29px;
      }
      .el-icon {
        cursor: pointer;
      }
    }
  }

  .number-available {
    font-size: 0.85em;
    color: var(--el-color-primary-light-3);
    background: transparent;
  }

  .quadrant-location-label {
    font-weight: 450;
  }

  .quadrant-location-disabled,
  .el-checkbox.is-disabled,
  .el-checkbox__input.is-disabled .el-checkbox__inner {
    color: var(--el-disabled-text-color);
  }
}
</style>
